import React, { useState } from "react";
import { Box, Button, IconButton, Modal, TextField, Typography } from "@mui/material";
import DropDown from "../baseComponents/dropdown";
import { textPattern } from "../../constants/patterns";
import modalStyle from "../../styles/modalStyle";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { FieldRequest } from "../../interfaces/field";
import CloseIcon from '@mui/icons-material/Close';
import apiClient from "../../axiosClient";
import FieldService from "../../services/fieldService";

interface AddFieldModalProps {
    modalVisible: boolean;
    closeModal: (newFieldId?: string) => void;
}

const AddFieldModal: React.FC<AddFieldModalProps> = ({ 
    modalVisible, 
    closeModal
}) => {
    const picklists = useSelector((state: RootState) => state.picklists.picklists);
    // Only for client_admin users (non-superadmin)
    
    // Form state using FieldRequest interface
    const [fieldData, setFieldData] = useState<FieldRequest>({
        field_name: '',
        field_label: '',
        field_type: '',
        field_description: '',
        field_value_type: '',
        is_standard: false
    });
    const [loading, setLoading] = useState<boolean>(false);
    

    const handleCloseModal = (newFieldId?: string) => {
        // Reset form state
        setFieldData({
            field_name: '',
            field_label: '',
            field_type: '',
            field_description: '',
            field_value_type: '',
            is_standard: false
        });
        setLoading(false);
        if (newFieldId) {
            closeModal(newFieldId);
        } else {
            closeModal();
        }
    };

    const validateForm = () => {
        if (!fieldData.field_name.trim()) {
            alert('Please enter a field name');
            return false;
        }
        if (!fieldData.field_label.trim()) {
            alert('Please enter a field label');
            return false;
        }
        if (!fieldData.field_type) {
            alert('Please select a field type');
            return false;
        }
        if (!fieldData.field_value_type) {
            alert('Please select a value type');
            return false;
        }
        if (!fieldData.field_description.trim()) {
            alert('Please enter a field description');
            return false;
        }
        
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const response = await apiClient.post('/api/v1/field', fieldData);

            if (response.status === 200) {
                alert('Field created successfully');
                // Get the newly created field ID from response
                const newFieldId = response.data?.field_id;
                // Immediately update Redux with latest fields
                await FieldService.updateFieldsInRedux();
                // Pass the new field ID to close handler
                handleCloseModal(newFieldId);
            } else {
                alert('Failed to create field');
            }
        } catch (error: any) {
            let errorMessage = 'An error occurred';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data) {
                errorMessage = error.response.data;
            } else if (error.message) {
                errorMessage = error.message;
            }
            alert(`Error: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={modalVisible} onClose={() =>handleCloseModal()}>
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography id="modal-title" variant="h6">
                        Add New Field
                    </Typography>
                    <IconButton onClick={() => handleCloseModal()} disabled={loading}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box mt={2} display="flex" justifyContent="space-between">
                    <Box width="48%">
                        <TextField
                            label="Field Name"
                            value={fieldData.field_name}
                            onChange={(e) => setFieldData(prev => ({
                                ...prev,
                                field_name: e.target.value.replace(textPattern, "")
                            }))}
                            fullWidth
                            margin="normal"
                            required
                            disabled={loading}
                            slotProps={{
                                htmlInput: {
                                    maxLength: 100,
                                },
                            }}
                        />
                    </Box>
                    <Box width="48%">
                        <TextField
                            label="Field Label"
                            value={fieldData.field_label}
                            onChange={(e) => setFieldData(prev => ({
                                ...prev,
                                field_label: e.target.value.replace(textPattern, "")
                            }))}
                            fullWidth
                            margin="normal"
                            required
                            helperText="User-friendly display name for the field"
                            disabled={loading}
                            slotProps={{
                                htmlInput: {
                                    maxLength: 100,
                                },
                            }}
                        />
                    </Box>
                </Box>

                <Box mt={2} display="flex" justifyContent="space-between">
                    <Box width="48%">
                        <DropDown 
                            label="Field Type" 
                            items={["SECONDARY", "TERTIARY"]} 
                            selectedValue={fieldData.field_type} 
                            onSelection={(value) => setFieldData(prev => ({
                                ...prev,
                                field_type: value
                            }))}
                            disabled={loading}
                        />
                    </Box>
                    <Box width="48%">
                        <DropDown 
                            label="Select Value Type" 
                            items={picklists?.VALUE_TYPE || []} 
                            selectedValue={fieldData.field_value_type}
                            onSelection={(value) => setFieldData(prev => ({
                                ...prev,
                                field_value_type: value
                            }))}
                            disabled={loading}
                        />
                    </Box>
                </Box>

                <Box mt={2} display="flex" justifyContent="space-between">
                    <Box width="100%">
                        <TextField
                            label="Field Description"
                            value={fieldData.field_description}
                            onChange={(e) => setFieldData(prev => ({
                                ...prev,
                                field_description: e.target.value.replace(textPattern, "")
                            }))}
                            fullWidth
                            margin="normal"
                            required
                            disabled={loading}
                            slotProps={{
                                htmlInput: {
                                    maxLength: 500,
                                },
                            }}
                        />
                    </Box>
                </Box>

                <Box mt={2} textAlign="right">
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? 'Creating...' : 'Create Field'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddFieldModal;
